<script setup>

import { defineProps, defineEmits, ref, computed} from "vue";
import CartaoDePedidoDeCompra from "./CartaoDePedidoDeCompra/CartaoDePedidoDeCompra.vue";
import TabelaDeAprovadores from "./Aprovadores/TabelaDeAprovadores.vue";
import CartaoObservacaoParaRevisao from "./ObservacaoParaRevisao/CartaoObservacaoParaRevisao.vue";
import CartaoFornecedor from "./Fornecedor/CartaoFornecedor.vue";
import TabelaDeItens from "./Itens/TabelaDeItens.vue";
import TabelaDeParcelas from "./Parcelas/TabelaDeParcelas.vue";
import CartaoObservacoesGerais from "./ObservacoesGerais/CartaoObservacoesGerais.vue";
import UtilNumber from "../../../utils/UtilNumber";


//==================================================================//
//========================= Props & Emits ==========================//
//==================================================================//

const props = defineProps([
    "revisandoPedido",
    "pedidoDeCompra",
    "fornecedores",
    "parcelasCondicoesDePagamento",
    "itensUnidades",
    "cotacoesDePedidoDeCompra",
    "somaValorTotal",
    "somaCustoAdicional",
    "somaValorSobressalente",
    "somaParcelasNovasValor",
    "somaValorRestante",
    "controlesDesativados"
]);

const emits = defineEmits([
    "update:alteraFornecedor",
    "update:trocaFornecedor",
    "update:fornecedorVendedor",
    "update:fornecedorUltimaObservacao",
    "update:itens",
    "update:item",
    "update:adicionaItem",
    "update:removeItem",
    "update:cotacaoDePedidoDeCompraItem",
    "update:adicionaParcela",
    "update:alteraParcela",
    "update:removeParcelaRascunho",
    "update:observacoesGerais",
    "update:controlesDesativados"
]);

//==================================================================//
//=================== Tabela de Aprovadores ========================//
//==================================================================//

const tabelaDeAprovadores = ref(null);

//==================================================================//
//================= Observação para Revisão ========================//
//==================================================================//

const observacaoParaRevisao = ref(null);

//==================================================================//
//======================= Cartão Fornecedor ========================//
//==================================================================//

const cartaoFornecedor = ref(null);

//==================================================================//
//======================== Tabela de Itens =========================//
//==================================================================//

const tabelaDeItens = ref(null);

//==================================================================//
//====================== Tabela de Parcelas ========================//
//==================================================================//

const tabelaDeParcelas = ref(null);

//==================================================================//
//=================== Cartão Observações Gerais ====================//
//==================================================================//

const cartaoObservacoesGerais = ref(null);

//==================================================================//
//================== Altera Controles Desativados ==================//
//==================================================================//

const alteraControlesDesativados = (update) =>{

    emits('update:controlesDesativados',update);

}

//==================================================================//
//====================== Altera Fornecedor =========================//
//==================================================================//

const alteraFornecedor = (update)=>{

    emits('update:alteraFornecedor',update);

};

//==================================================================//
//====================== Troca Fornecedor =========================//
//==================================================================//

const trocaFornecedor = (update)=>{

    emits('update:trocaFornecedor',update);

};

//==================================================================//
//================= Altera Fornecedor Vendedor =====================//
//==================================================================//

const alteraFornecedorVendedor = (update)=>{

    emits('update:fornecedorVendedor',update);

};

//==================================================================//
//================= Altera Observações Gerais ======================//
//==================================================================//

const alteraFornecedorUltimaObservacao = (update)=>{

    emits('update:fornecedorUltimaObservacao',update);

};

//==================================================================//
//======================== Altera Itens ============================//
//==================================================================//

const alteraItens = (update)=>{

    emits('update:itens',update);

};


//==================================================================//
//======================== Altera Item ============================//
//==================================================================//

const alteraItem = (update)=>{

    emits('update:item',update);

};

//==================================================================//
//========================= Adiciona Item ==========================//
//==================================================================//

const adicionaItem = (update) =>{

    emits('update:adicionaItem', update);

};

//==================================================================//
//=========================== Remove Item ==========================//
//==================================================================//

const removeItem = (update = {}) =>{

    emits('update:removeItem', update);

};

//==================================================================//
//============ Altera Cotacao Pedido de Compra Item ================//
//==================================================================//

const alteraCotacaoDePedidoDeCompraItem = (update) =>{

    emits('update:cotacaoDePedidoDeCompraItem',update);

};

//==================================================================//
//====================== Adiciona Parcela ==========================//
//==================================================================//

const adicionaParcela = (update)=>{

    emits('update:adicionaParcela',update);

};

//==================================================================//
//====================== Altera Parcela ============================//
//==================================================================//

const alteraParcela = (update)=>{

    emits('update:alteraParcela',update);

};

//==================================================================//
//================= Remove Parcela Rascunho ========================//
//==================================================================//

const removeParcelaRascunho = (update)=>{

    emits('update:removeParcelaRascunho',update);

};

//==================================================================//
//================= Altera Observações Gerais ======================//
//==================================================================//

const alteraObservacoesGerais = (update)=>{

    emits('update:observacoesGerais',update);

};

//==================================================================//
//================== Soma Itens Valor Disponivel ===================//
//==================================================================//

let somaItensValorDisponivel = computed(()=>{

    return props?.pedidoDeCompra?.itens?.reduce((acc,{osItemValorDisponivel})=>{

        if(osItemValorDisponivel){
            acc += osItemValorDisponivel;
        }
        
        return acc;

    },0) || 0;

});

//==================================================================//
//=========== Soma Itens Valor Disponivel Formatado ================//
//==================================================================//

let somaItensValorDisponivelFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(somaItensValorDisponivel.value,-999999999,999999999));

});

//==================================================================//
//================ Soma Valor Total Formatado ======================//
//==================================================================//

let somaValorTotalFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(props.somaValorTotal,-999999999,999999999));

});

//==================================================================//
//================ Soma Custo Adicional Formatado ==================//
//==================================================================//

let somaCustoAdicionalFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(props.somaCustoAdicional,-999999999,999999999));

});

//==================================================================//
//============= Soma Valor Sobressalente Formatado =================//
//==================================================================//

let somaValorSobressalenteFormatado = computed(()=>{

    return new Intl.NumberFormat('default', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(UtilNumber.clamp(props.somaValorSobressalente,-999999999,999999999));

});

</script>

<template>

    <div class="c-conteudo">

        <CartaoDePedidoDeCompra
            :dado="props?.pedidoDeCompra"
        ></CartaoDePedidoDeCompra>

        <TabelaDeAprovadores
            ref="tabelaDeAprovadores"
            :pedidoDeCompra="props?.pedidoDeCompra"
        ></TabelaDeAprovadores>
        
        <CartaoObservacaoParaRevisao
            ref="tabelaDeAprovadores"
            :pedidoDeCompra="props?.pedidoDeCompra"
        >
        </CartaoObservacaoParaRevisao>

        <CartaoFornecedor
            ref="cartaoFornecedor"
            :revisandoPedido="props.revisandoPedido"
            :pedidoDeCompra="props.pedidoDeCompra"
            :fornecedores="props.fornecedores"
            :controlesDesativados="props.controlesDesativados"
            @update:alteraFornecedor="alteraFornecedor"
            @update:trocaFornecedor="trocaFornecedor"
            @update:fornecedorVendedor="alteraFornecedorVendedor"
        ></CartaoFornecedor>

        <TabelaDeItens
            ref="tabelaDeItens"
            :revisandoPedido="props.revisandoPedido"
            :pedidoDeCompra="props.pedidoDeCompra"
            :somaItensValorDisponivelFormatado="somaItensValorDisponivelFormatado"
            :somaValorTotalFormatado="somaValorTotalFormatado"
            :somaValorSobressalenteFormatado="somaValorSobressalenteFormatado"
            :somaCustoAdicionalFormatado="somaCustoAdicionalFormatado"
            :itensUnidades="props.itensUnidades"
            :cotacoesDePedidoDeCompra="props.cotacoesDePedidoDeCompra"
            :controlesDesativados="props.controlesDesativados"
            @update:itens="alteraItens"
            @update:item="alteraItem"
            @update:adicionaItem="adicionaItem"
            @update:removeItem="removeItem"
            @update:cotacaoDePedidoDeCompraItem="alteraCotacaoDePedidoDeCompraItem"
        ></TabelaDeItens>

        <TabelaDeParcelas
            ref="tabelaDeParcelas"
            :revisandoPedido="props.revisandoPedido"
            :pedidoDeCompra="props.pedidoDeCompra"
            :somaValorTotal="props.somaValorTotal"
            :somaValorTotalFormatado="somaValorTotalFormatado"
            :somaValorSobressalente="props.somaValorSobressalente"
            :somaCustoAdicional="props.somaCustoAdicional"
            :somaParcelasNovasValor="props.somaParcelasNovasValor"
            :somaValorRestante="props.somaValorRestante"
            :parcelasCondicoesDePagamento="props.parcelasCondicoesDePagamento"
            :controlesDesativados="props.controlesDesativados"
            @update:adicionaParcela="adicionaParcela"
            @update:alteraParcela="alteraParcela"
            @update:removeParcelaRascunho="removeParcelaRascunho"
        ></TabelaDeParcelas>

        <CartaoObservacoesGerais
            ref="cartaoObservacoesGerais"
            :revisandoPedido="props.revisandoPedido"
            :pedidoDeCompra="props.pedidoDeCompra"
            :controlesDesativados="props.controlesDesativados"
            @update:observacoesGerais="alteraObservacoesGerais"
            @update:controlesDesativados="alteraControlesDesativados"
            @update:fornecedorUltimaObservacao="alteraFornecedorUltimaObservacao"
        >
        </CartaoObservacoesGerais>

    </div>

</template>

<style scoped>

.c-conteudo{
    display: grid;
    gap: 15px;
}

</style>